import { ReactElement } from 'react'
import { Vector3 } from 'three'
import { Delete, HorizontalRule, TurnSharpRight } from '@mui/icons-material'
import { Line } from '@react-three/drei'
import CommentCalloutArrow from '../CommentCalloutArrow'
import SceneHud from '../SceneHud'

// Small z-lift matching Comment3D's callout rendering, to avoid z-fighting.
const CALLOUT_Z_OFFSET = 0.02

// Blue highlight for hovered/selected callouts, with a soft wider halo on hover
// (à la the ui-toolkit hoverable lines).
const HIGHLIGHT_COLOR = '#2196f3'
const HOVER_HALO_WIDTH = 6
const HOVER_HALO_OPACITY = 0.3

interface Props {
  comment: Comment3D
  selectedIndex: number | null
  hoveredIndex: number | null
  onHover: (index: number | null) => void
  onSelect: (index: number) => void
  onDelete: (index: number) => void
  onAddKnee: (index: number) => void
  onRemoveKnee: (index: number) => void
}

const toVector = (point: Point): Vector3 => new Vector3(point.x, point.y, point.z)

/**
 * Interactive callout arrows for the active comment: hovering highlights a
 * callout, clicking selects it (highlighted in the primary color), and a delete
 * button appears at the selected callout's center to remove it.
 *
 * Comment3D hides its own (static) callouts for the active comment so these
 * replace them rather than drawing on top.
 */
const CommentCalloutControls = ({
  comment,
  selectedIndex,
  hoveredIndex,
  onHover,
  onSelect,
  onDelete,
  onAddKnee,
  onRemoveKnee,
}: Props): ReactElement => {
  const center = toVector(comment.position)
  const callouts = comment.callouts ?? []

  return (
    <group position={new Vector3(0, 0, CALLOUT_Z_OFFSET)}>
      {callouts.map((callout, index) => {
        const end = toVector(callout.end)
        const knee = callout.knee ? toVector(callout.knee) : null
        const isSelected = selectedIndex === index
        const isHovered = hoveredIndex === index
        const color = isSelected || isHovered ? HIGHLIGHT_COLOR : comment.color
        const pathPoints = knee ? [center, knee, end] : [center, end]
        // Anchor the action buttons at the knee when present, otherwise at the
        // midpoint of the (straight) callout line.
        const buttonAnchor = knee ?? center.clone().add(end).multiplyScalar(0.5)

        return (
          <group key={index}>
            {(isHovered || isSelected) && (
              <Line
                points={pathPoints}
                color={HIGHLIGHT_COLOR}
                lineWidth={HOVER_HALO_WIDTH}
                transparent
                opacity={HOVER_HALO_OPACITY}
              />
            )}
            {knee ? (
              <>
                <CommentCalloutArrow
                  from={center}
                  to={knee}
                  color={color}
                  id={comment.id}
                  index={index * 2}
                  arrowHead={false}
                />
                <CommentCalloutArrow
                  from={knee}
                  to={end}
                  color={color}
                  id={comment.id}
                  index={index * 2 + 1}
                  arrowHead
                />
              </>
            ) : (
              <CommentCalloutArrow
                from={center}
                to={end}
                color={color}
                id={comment.id}
                index={index}
                arrowHead
              />
            )}

            {/* Invisible wide line that catches hover/click for easy picking. */}
            <Line
              points={pathPoints}
              transparent
              opacity={0}
              lineWidth={14}
              onPointerOver={event => {
                event.stopPropagation()
                onHover(index)
              }}
              onPointerOut={() => onHover(null)}
              onClick={event => {
                event.stopPropagation()
                onSelect(index)
              }}
            />

            {isSelected && (
              <SceneHud
                position={buttonAnchor}
                transform="translate(-50%, -50%)"
                zIndexRange={[95, 0]}
                groups={[
                  [
                    {
                      key: 'add-knee',
                      tooltip: 'Knie hinzufügen',
                      icon: <TurnSharpRight fontSize="small" />,
                      onClick: () => onAddKnee(index),
                      dataCy: 'comment-callout-add-knee',
                    },
                    ...(knee
                      ? [
                          {
                            key: 'remove-knee',
                            tooltip: 'Knie entfernen',
                            icon: <HorizontalRule fontSize="small" />,
                            onClick: () => onRemoveKnee(index),
                            dataCy: 'comment-callout-remove-knee',
                          },
                        ]
                      : []),
                  ],
                  [
                    {
                      key: 'delete',
                      tooltip: 'Verweispfeil löschen',
                      icon: <Delete fontSize="small" />,
                      onClick: () => onDelete(index),
                      variant: 'danger',
                      dataCy: 'comment-callout-delete',
                    },
                  ],
                ]}
              />
            )}
          </group>
        )
      })}
    </group>
  )
}

export default CommentCalloutControls
